import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageSquare, Send, User } from 'lucide-react';
import { timeAgo } from '../lib/utils';
import VoiceRecorder from '../components/VoiceRecorder';
import VideoCall from '../components/VideoCall';

export default function MessagesPage() {
  const { user, axiosAuth } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [showOversight, setShowOversight] = useState(false);
  const bottomRef = useRef(null);
  const api = axiosAuth();
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    api.get('/messages/contacts').then(r => {
      setContacts(r.data);
      if (r.data.length > 0 && !activeContact) setActiveContact(r.data[0]);
    }).catch(() => {});
    if (isAdmin) {
      api.get('/messages/oversight').then(r => setAllMessages(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (activeContact) {
      api.get(`/messages?contact_id=${activeContact.id}`).then(r => setMessages(r.data)).catch(() => {});
      const interval = setInterval(() => {
        api.get(`/messages?contact_id=${activeContact.id}`).then(r => setMessages(r.data)).catch(() => {});
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeContact]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || !activeContact) return;
    setSending(true);
    try {
      await api.post('/messages', { receiver_id: activeContact.id, content: newMsg.trim() });
      setNewMsg('');
      const r = await api.get(`/messages?contact_id=${activeContact.id}`);
      setMessages(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        const { toast } = await import('sonner');
        toast.error(detail);
      }
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Messages</h1>
        {isAdmin && (
          <button
            onClick={() => setShowOversight(!showOversight)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${showOversight ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-teal-600 border-teal-300 hover:bg-teal-50'}`}
            data-testid="oversight-toggle"
          >
            {showOversight ? 'Close Oversight View' : 'View All Messages (Oversight)'}
          </button>
        )}
      </div>
      
      {showOversight && isAdmin ? (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <p className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>All Platform Messages (Executive Oversight)</p>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {allMessages.length > 0 ? allMessages.map(m => (
                <div key={m.id} className="p-3 rounded-xl bg-muted/30 border border-border/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-teal-700">{m.sender_name}</span>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(m.timestamp)}</span>
                  </div>
                  <p className="text-sm">{m.content}</p>
                </div>
              )) : <p className="text-sm text-muted-foreground">No messages on the platform yet.</p>}
            </div>
          </CardContent>
        </Card>
      ) : (
      <div className="grid md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-12rem)]">
        {/* Contact List */}
        <Card className="border-0 shadow-sm rounded-2xl overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-3 space-y-1">
              {contacts.length > 0 ? contacts.map(c => (
                <button
                  key={c.id}
                  onClick={() => setActiveContact(c)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${activeContact?.id === c.id ? 'bg-teal-50 border border-teal-200' : 'hover:bg-muted/50'}`}
                  data-testid={`contact-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-semibold text-teal-700 shrink-0">
                    {c.full_name?.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.full_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{c.role?.replace('_', ' ')}</p>
                  </div>
                </button>
              )) : (
                <div className="p-6 text-center"><User className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No contacts yet</p></div>
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Chat Area */}
        <Card className="border-0 shadow-sm rounded-2xl flex flex-col overflow-hidden">
          {activeContact ? (
            <>
              <div className="px-4 py-3 border-b bg-white/80 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{activeContact.full_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{activeContact.role?.replace('_', ' ')}</p>
                </div>
                {user.role === 'client' && (
                  <VideoCall contactId={activeContact.id} contactName={activeContact.full_name} onEnd={() => {}} />
                )}
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map(m => (
                    <div key={m.id} className={`flex ${m.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-sm ${m.sender_id === user.id ? 'bg-teal-600 text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                        <p>{m.content}</p>
                        <p className={`text-[10px] mt-1 ${m.sender_id === user.id ? 'text-teal-100' : 'text-muted-foreground'}`}>{timeAgo(m.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
              </ScrollArea>
              <div className="p-3 border-t bg-white">
                <form onSubmit={e => { e.preventDefault(); handleSend(); }} className="flex gap-2">
                  <VoiceRecorder size="icon" onTranscription={t => setNewMsg(prev => prev ? `${prev} ${t}` : t)} />
                  <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type a message..." className="flex-1 rounded-full" data-testid="message-input" />
                  <Button type="submit" size="icon" disabled={sending || !newMsg.trim()} className="bg-teal-600 hover:bg-teal-700 rounded-full shrink-0" data-testid="send-message-button">
                    <Send size={16} />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center"><MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">Select a contact to start messaging</p></div>
            </div>
          )}
        </Card>
      </div>
      )}
      {/* Disclaimer */}
      <div className="mt-4 px-3 py-2 text-center">
        <p className="text-xs text-muted-foreground">Messages and videos may be monitored for safety and compliance.</p>
      </div>
    </div>
  );
}
